import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { CSSTransition } from "react-transition-group";
import { X } from "lucide-react";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import StockIssueCompactRow from "./StockIssueCompactRow";
import PDFExportModal from "./PDFExportModal";
import { toWarehouseCodes } from "./warehouseFilters";
import { projectHasDrift, computeIssueDrift } from "./contactDrift";
import { MiniButton } from "components/standardElements";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT,
} from "helpers/designConstants";

const SEVERITY_TOKENS = {
  critical: {
    color: COLORS.alertRed,
    bg: "rgba(204, 0, 0, 0.12)",
  },
  warning: {
    color: COLORS.alertOrange,
    bg: "rgba(247, 147, 30, 0.15)",
  },
};

function countBySeverity(issues) {
  const counts = { critical: 0, warning: 0 };
  for (const i of issues) {
    if (counts[i.severity] != null) counts[i.severity] += 1;
  }
  return counts;
}

/**
 * Right-side sheet — not a centered modal. Matters because the inventory
 * detail view is itself a modal. A modal-over-modal reads as broken layering;
 * a modal-over-sheet reads as proper progressive disclosure (the sheet is the
 * "page" you're on, the modal is the zoom-in).
 */
export default function StockIssueDetailDialog({
  show,
  onClose,
  project,
  productsById,
  onUpdateStatus,
  onBatchUpdate,
}) {
  const nodeRef = useRef();
  const [projectWarehouses, setProjectWarehouses] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!show || !project?.firebaseId) return;
    let cancelled = false;
    getDoc(doc(getFirestore(), "projects", project.firebaseId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        setProjectWarehouses(toWarehouseCodes(snap.data().warehouses));
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load project warehouses", e);
      });
    return () => {
      cancelled = true;
    };
  }, [show, project?.firebaseId]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  if (!project) return null;

  const severityCounts = countBySeverity(project.issues);
  const hasDrift = projectHasDrift(project);
  const metaLine = [project.distributor, project.accountNumber, project.rep]
    .filter(Boolean)
    .join(" · ");

  // Same eligibility rule as the project card: new/acknowledged always;
  // contacted only when drifted (re-stamp to ack the update). Dismissed
  // stays dismissed.
  const eligibleForContacted = project.issues
    .filter((i) => {
      if (i.status === "new" || i.status === "acknowledged") return true;
      if (i.status === "contacted") return computeIssueDrift(i).drifted;
      return false;
    })
    .map((i) => i.id);

  const handleMarkContacted = async () => {
    if (eligibleForContacted.length === 0 || !onBatchUpdate) return;
    setBusy(true);
    try {
      const result = await onBatchUpdate(eligibleForContacted, "contacted");
      const updated = result.updated?.length ?? 0;
      const skipped = result.skipped?.length ?? 0;
      if (skipped > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `Batch contacted: ${updated} updated, ${skipped} skipped`,
          result.skipped,
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return ReactDOM.createPortal(
    <>
    <CSSTransition
      nodeRef={nodeRef}
      in={show}
      unmountOnExit
      timeout={{ enter: 0, exit: 280 }}
    >
      <Root ref={nodeRef} role="dialog" aria-modal="true">
        <Backdrop onClick={onClose} />
        <Sheet>
          <Header>
            <TitleBlock>
              <ProjectName>{project.name}</ProjectName>
              {metaLine && <ProjectMeta>{metaLine}</ProjectMeta>}
            </TitleBlock>
            <HeaderRight>
              <Counts>
                {["critical", "warning"].map((s) =>
                  severityCounts[s] > 0 ? (
                    <SeverityChip
                      key={s}
                      color={SEVERITY_TOKENS[s].color}
                      bg={SEVERITY_TOKENS[s].bg}
                    >
                      {severityCounts[s]} {s}
                    </SeverityChip>
                  ) : null,
                )}
                {hasDrift && <UpdatedChip>Updated</UpdatedChip>}
                <TotalCount>
                  {project.issues.length} issue
                  {project.issues.length === 1 ? "" : "s"}
                </TotalCount>
              </Counts>
              <Actions>
                <MiniButton
                  type="button"
                  onClick={() => setShowPdfModal(true)}
                >
                  Export PDF
                </MiniButton>
                {onBatchUpdate && (
                  <MiniButton
                    type="button"
                    onClick={handleMarkContacted}
                    disabled={busy || eligibleForContacted.length === 0}
                  >
                    {busy ? "Marking..." : "Mark contacted"}
                  </MiniButton>
                )}
              </Actions>
            </HeaderRight>
            <CloseButton
              type="button"
              onClick={onClose}
              aria-label="Close detail"
            >
              <X size={20} />
            </CloseButton>
          </Header>

          <Body>
            {project.issues.map((issue) => (
              <StockIssueCompactRow
                key={issue.id}
                issue={issue}
                product={productsById[issue.product_id]}
                projectFirebaseId={project.firebaseId}
                projectWarehouses={projectWarehouses}
                onUpdateStatus={onUpdateStatus}
              />
            ))}
          </Body>
        </Sheet>
      </Root>
    </CSSTransition>
    <PDFExportModal
      show={showPdfModal}
      onClose={() => setShowPdfModal(false)}
      project={project}
      productsById={productsById}
    />
    </>,
    document.getElementById("root"),
  );
}

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
  overflow: hidden;

  &.enter-done {
    pointer-events: auto;
  }
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: opacity 0.25s ease-out;

  ${Root}.enter-done & {
    opacity: 1;
  }

  ${Root}.exit & {
    opacity: 0;
  }
`;

const Sheet = styled.div`
  position: relative;
  height: 100%;
  width: 72vw;
  max-width: 900px;
  min-width: 520px;
  background: ${COLORS.primaryInverted};
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);

  ${Root}.enter-done & {
    transform: translateX(0);
  }

  ${Root}.exit & {
    transform: translateX(100%);
  }

  @media (max-width: 640px) {
    width: 100vw;
    min-width: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px 24px;
  border-bottom: 1px solid ${COLORS.secondary};
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  flex: 1 1 240px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProjectName = styled.h2`
  margin: 0;
  font-size: ${FONTSIZE.large};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ProjectMeta = styled.div`
  font-size: ${FONTSIZE.small};
  color: ${COLORS.stormtechGrey};
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
`;

const Counts = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SeverityChip = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ color }) => color};
  background: ${({ bg }) => bg};
  border: 0;
`;

const TotalCount = styled.span`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
  text-transform: uppercase;
  font-weight: ${FONTWEIGHT.medium};
`;

const UpdatedChip = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${COLORS.text};
  background: ${COLORS.tertiary};
  border: 1px solid ${COLORS.secondary};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: ${COLORS.stormtechGrey};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${COLORS.tertiary};
    color: ${COLORS.text};
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 24px 24px;
`;
